/**
 * Area — Dati (PR-ONB-RESUME-1B).
 *
 * Resume entry point: loads the FULL server draft, stashes it for the
 * wizard (the form instance lives under /registrazione-intermediario) and
 * navigates to the saved step. Editable only while the request is a DRAFT;
 * from SUBMITTED on, the dossier is locked server-side and this page says so.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner } from '@ab-platform/ui/banner';
import { FieldError } from '@ab-platform/ui/field-error';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { PillButton } from '@ab-platform/ui/pill-button';
import { useAreaSession } from '@/hooks/AreaSessionContext';
import { useAreaMe } from '@/pages/area/areaMeContext';
import { getAreaRequest, createAreaDraft } from '@/lib/areaApi';
import { resumeStepPath } from '@/lib/accountBindFlow';
import { rejectedCooldownView } from '@/lib/areaStatusView';
import { saveResumeDraftStash } from '@/lib/resumeDraftStash';

export function AreaDataPage(): JSX.Element {
  const session = useAreaSession();
  const navigate = useNavigate();
  const { me, refresh } = useAreaMe();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = me.request;
  // DRAFT = full editing; NEEDS_CORRECTION = TARGETED editing
  // (PR-ONB-PRECHECK-3): the resume carries the OPEN issues and the server
  // accepts only the reopened fields.
  const isEditable =
    request?.status === 'DRAFT' || request?.status === 'NEEDS_CORRECTION';
  const needsCorrection = request?.status === 'NEEDS_CORRECTION';
  // REJECTED is NOT a generic closed state (review fix #6): the 6-month
  // cooldown applies and the restart date must be surfaced explicitly.
  const isRejected = request?.status === 'REJECTED';
  const cooldown = isRejected
    ? rejectedCooldownView(request.cooldownUntil, Date.now())
    : null;
  const isClosed =
    request !== null &&
    ['WITHDRAWN', 'EXPIRED', 'CANCELLED'].includes(request.status);
  const canStartNew = isClosed || (isRejected && cooldown !== null && !cooldown.blocked);

  const handleResume = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const token = await session.getToken();
      if (!token) {
        setError('Sessione scaduta: accedi di nuovo.');
        return;
      }
      const result = await getAreaRequest(token);
      if (!result.ok) {
        setError('Caricamento bozza non riuscito. Riprova.');
        return;
      }
      saveResumeDraftStash({
        draftPayload: result.data.draftPayload ?? {},
        draftRevision: result.data.draftRevision,
        requestPublicId: result.data.publicId,
        currentStep: result.data.draftState?.currentStep ?? null,
        // PR-ONB-PRECHECK-3 — the wizard renders the per-step correction
        // banners from these (empty on a plain DRAFT resume).
        ...(result.data.openIssues.length > 0 && {
          openIssues: result.data.openIssues,
        }),
      });
      session.markBound({
        requestPublicId: result.data.publicId,
        draftRevision: result.data.draftRevision,
      });
      navigate(resumeStepPath(result.data.draftState?.currentStep));
    } finally {
      setBusy(false);
    }
  };

  const handleNewDraft = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const token = await session.getToken();
      if (!token) {
        setError('Sessione scaduta: accedi di nuovo.');
        return;
      }
      const result = await createAreaDraft(token);
      if (!result.ok) {
        setError(
          result.kind === 'COOLDOWN_ACTIVE'
            ? 'La tua precedente richiesta è stata respinta: non è ancora possibile presentarne una nuova.'
            : 'Creazione nuova bozza non riuscita. Riprova.',
        );
        return;
      }
      refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <FocusedPageShell
      title="Dati della richiesta"
      description="Riprendi la compilazione da dove l'avevi lasciata: ogni modifica viene salvata automaticamente."
      footer={
        isEditable ? (
          <PillButton
            type="button"
            size="lg"
            className="w-full"
            disabled={busy}
            onClick={() => void handleResume()}
          >
            Riprendi la compilazione
          </PillButton>
        ) : canStartNew ? (
          <PillButton
            type="button"
            size="lg"
            className="w-full"
            disabled={busy}
            onClick={() => void handleNewDraft()}
          >
            Apri una nuova richiesta
          </PillButton>
        ) : undefined
      }
    >
      <div className="grid gap-3">
        {!request ? (
          <Banner tone="info" title="Nessuna richiesta">
            Non hai una richiesta in corso.
          </Banner>
        ) : needsCorrection ? (
          <Banner tone="warning" title="Correzioni richieste">
            Il backoffice ha richiesto alcune correzioni: riprendi la
            compilazione per aggiornare i campi indicati e ripresentare la
            richiesta. Puoi modificare solo i punti riaperti.
          </Banner>
        ) : isEditable ? (
          <Banner tone="info" title="Bozza in corso">
            La tua richiesta è in bozza: puoi modificare i dati fino all'invio
            per il controllo preliminare.
          </Banner>
        ) : isRejected && cooldown ? (
          <Banner tone="warning" title="Richiesta respinta">
            {cooldown.blocked
              ? cooldown.dateLabel
                ? `Potrai presentare una nuova richiesta a partire dal ${cooldown.dateLabel}.`
                : 'Non è al momento possibile presentare una nuova richiesta. Trovi i dettagli nell\'email ricevuta.'
              : 'Il periodo di attesa è terminato: puoi presentare una nuova richiesta.'}
          </Banner>
        ) : isClosed ? (
          <Banner tone="info" title="Richiesta chiusa">
            La richiesta precedente è chiusa. Puoi aprirne una nuova quando
            vuoi.
          </Banner>
        ) : (
          <Banner tone="info" title="Dati bloccati">
            La richiesta è stata inviata: i dati non sono più modificabili da
            qui. Se servono correzioni, riceverai un'email con le indicazioni.
          </Banner>
        )}
        {error ? <FieldError message={error} /> : null}
      </div>
    </FocusedPageShell>
  );
}
